import { Component, OnInit, OnDestroy, inject, Input, EventEmitter, Output, ElementRef } from '@angular/core';

import { HttpClient } from '@angular/common/http';
import { PSIC_COMPONENTS } from '@pacificspecialtyinsurancecompany/psic-ng-components';
import { Store } from '@ngrx/store';
import { TableCardLayoutComponent } from '../shared/table-card-layout/table-card-layout.component';
import { PolicyState } from '../store/policy/policy.reducer';
import { combineLatest, Subscription } from 'rxjs';
import { filter, take } from 'rxjs/operators';
import { DocumentService } from '../services/document.service';
import { getAuthToken } from '../auth.interceptor';
import { finalize } from 'rxjs/operators';
import { LoadingService } from '../shared/loading.service';
import { ToastService } from '../shared/toast/toast.service';
import { MatDialog } from '@angular/material/dialog';
import { PdfViewerComponent } from '../shared/pdf-viewer/pdf-viewer.component';
import { Renderer2 } from '@angular/core';
import { ScrollStrategyOptions } from '@angular/cdk/overlay';
import { MatIcon, MatIconRegistry } from '@angular/material/icon';
import { globalMessages } from '../shared/global-messages';

interface Inspection {
    dateOrdered: string;
    dateReceived: string;
    type: string;
    vendor: string;
}

interface Document {
    category: string;
    fileName: string;
    source: string;
    sendDate: string;
    receivedDate: string;
    type: string;
    attachmentDocId: string;
    attachmentDocType: string;
}

@Component({
    selector: 'app-inspections',
    imports: [...PSIC_COMPONENTS, MatIcon, TableCardLayoutComponent],
    templateUrl: './inspections.component.html',
    styleUrl: './inspections.component.scss',
})
export class InspectionsComponent implements OnInit, OnDestroy {
    @Input() isDCPolicyLoaded = false;
    @Input() dcPolicyFailed = false;
    @Output() retry = new EventEmitter<void>();
    globalMessages = globalMessages;
    inspections: Inspection[] = [];
    displayedInspections: Inspection[] = [];
    selectedRow: number | null = null;
    showAll = false;
    private docHubURL: string = '';
    private combinedSubscription: Subscription | undefined;
    private policyNo$ = this.store.select((state) => state.policy.policyNumber);
    private policyTerm$ = this.store.select((state) => state.policy.policyTerm);
    private documents: Document[] = [];
    private currentPolicyNo: string = '';
    private el = inject(ElementRef);
    private documentService = inject(DocumentService);
    private loader = inject(LoadingService);
    private toastService = inject(ToastService);
    private dialog = inject(MatDialog);
    private renderer = inject(Renderer2);
    private iconRegistry = inject(MatIconRegistry);
    private currentDialogRef: any = null;
    private dialogSubscriptions = new Subscription();

    constructor(
        private http: HttpClient,
        private store: Store<{ policy: PolicyState }>,
        private scrollStrategy: ScrollStrategyOptions
    ) {}

    ngOnInit() {
        this.http.get<any>('assets/config.json').subscribe((config) => {
            this.docHubURL = config.docHubURL;
        });

        this.combinedSubscription = combineLatest([
            this.store.select((state) => state.policy.data).pipe(filter((data) => !!data)),
            this.store.select((state) => state.policy.policyNumber).pipe(filter((policyNumber) => !!policyNumber)),
        ]).subscribe(([policyData, policyNumber]) => {
            this.currentPolicyNo = policyNumber!;
            this.loadInspections(policyData, policyNumber!);
            this.loadDocuments(policyData, policyNumber!);
        });
    }

    goToInspections() {
        combineLatest([this.policyNo$, this.policyTerm$])
            .pipe(take(1))
            .subscribe(([policyNo, term]) => {
                const termValue = term || '';
                window.open(
                    `${this.docHubURL}?SourceSystemName=DCPHX&policyNo=${policyNo}&term=${termValue}`,
                    '_blank'
                );
            });
    }

    private formatToMMDDYYYY(dateStr: string): string {
        if (!dateStr) return '';
        // M/D/YYYY or M/D/YYYY h:mm:ss AM/PM
        if (dateStr.includes('/')) {
            const [datePart] = dateStr.split(' ');
            const [m, d, y] = datePart.split('/');
            if (m && d && y) return `${m.padStart(2, '0')}-${d.padStart(2, '0')}-${y}`;
        }
        // YYYY-MM-DD
        if (dateStr.includes('-') && dateStr.indexOf('-') === 4) {
            const [y, m, d] = dateStr.split('-');
            if (m && d && y) return `${m}-${d}-${y}`;
        }
        return dateStr;
    }

    // Normalize any date string to YYYY-MM-DD for safe comparison
    private toISODate(dateStr: string): string {
        if (!dateStr) return '';
        // MM-DD-YYYY (inspection stored format)
        if (/^\d{2}-\d{2}-\d{4}$/.test(dateStr)) {
            const [m, d, y] = dateStr.split('-');
            return `${y}-${m}-${d}`;
        }
        // YYYY-MM-DD
        if (/^\d{4}-\d{2}-\d{2}$/.test(dateStr)) return dateStr;
        // M/D/YYYY or MM/DD/YYYY (with optional time)
        if (dateStr.includes('/')) {
            const [datePart] = dateStr.split(' ');
            const [m, d, y] = datePart.split('/');
            if (m && d && y) return `${y}-${m.padStart(2, '0')}-${d.padStart(2, '0')}`;
        }
        return '';
    }

    private loadInspections(policyData: any, policyKey: string) {
        const inspectionsData = policyData[policyKey]?.policySnapshot?.inspection || [];

        this.inspections = inspectionsData.map((i: any) => ({
            dateOrdered: this.formatToMMDDYYYY(i.dateOrdered?.value || ''),
            dateReceived: this.formatToMMDDYYYY(i.dateReceived?.value || ''),
            type: i.type?.value || '',
            vendor: i.vendor?.value || '',
        }));

        this.showAll = false;
        this.displayedInspections = this.inspections.slice(0, 3);
    }

    private loadDocuments(policyData: any, policyKey: string) {
        const documentsData = policyData[policyKey]?.dmsSnapshot || [];

        this.documents = documentsData.map((d: any) => ({
            category: d.category?.value || '',
            fileName: d.fileName?.value || '',
            source: d.source?.value || '',
            sendDate: d.sendDate?.value || '',
            receivedDate: d.receivedDate?.value || '',
            type: d.type?.value || '',
            attachmentDocId: d.attachmentDocId?.value || d.fileName?.value || '',
            attachmentDocType: d.attachmentDocType?.value || d.category?.value || 'Others',
        }));
    }

    /**
     * Returns true if the given ISO date string (YYYY-MM-DD) falls within
     * ±1 day of the target ISO date string.
     * Uses UTC day-number arithmetic to avoid any timezone offset issues.
     */
    private isWithinOneDay(targetISO: string, candidateISO: string): boolean {
        const toUTCDays = (iso: string): number => {
            const [y, m, d] = iso.split('-').map(Number);
            // UTC midnight — no local timezone offset involved
            return Date.UTC(y, m - 1, d) / (1000 * 60 * 60 * 24);
        };
        return Math.abs(toUTCDays(targetISO) - toUTCDays(candidateISO)) <= 1;
    }

    /**
     * Returns true if there is a matching inspection document
     * for the given inspection in the current term's documents.
     * Matches if the document received date is within ±1 day of the inspection received date.
     */
    hasInspectionDocument(inspection: Inspection): boolean {
        if (!inspection.dateReceived) return false;
        const inspectionISO = this.toISODate(inspection.dateReceived);
        if (!inspectionISO) return false;
        return this.documents.some((doc) => {
            if (!doc.receivedDate) return false;
            if (doc.category?.toLowerCase() !== 'inspection') return false;
            const docISO = this.toISODate(doc.receivedDate);
            if (!docISO) return false;
            return this.isWithinOneDay(inspectionISO, docISO);
        });
    }

    /**
     * Function to handle inspection document viewing
     * Matches inspection received date with document received date
     * Filters only documents with category "Inspection"
     * Focuses on the latest document by date and time
     */
    viewInspectionDocument(inspection: Inspection, index: number) {
        this.selectedRow = index;

        if (!inspection.dateReceived) {
            this.toastService.add({
                type: 'error',
                title: '',
                message: 'No received date found for this inspection.',
            });
            return;
        }

        // Filter documents that match the inspection received date AND category is "Inspection"
        const inspectionISO = this.toISODate(inspection.dateReceived);

        if (!inspectionISO) {
            this.toastService.add({
                type: 'error',
                title: '',
                message: 'Invalid inspection received date format.',
            });
            return;
        }
        const matchingDocuments = this.documents.filter((doc) => {
            if (!doc.receivedDate) return false;
            if (doc.category?.toLowerCase() !== 'inspection') return false;
            const docISO = this.toISODate(doc.receivedDate);
            if (!docISO) return false;
            return this.isWithinOneDay(inspectionISO, docISO);
        });

        if (matchingDocuments.length === 0) {
            this.toastService.add({
                type: 'error',
                title: '',
                message: 'No matching inspection document found for this date.',
            });
            return;
        }

        // Sort by latest date and time - focus on the most recent document
        const latestDocument = this.getLatestDocument(matchingDocuments);

        if (!latestDocument) {
            this.toastService.add({
                type: 'error',
                title: '',
                message: 'Unable to determine the latest document.',
            });
            return;
        }

        // View the PDF document
        this.viewDocument(latestDocument);
    }

    /**
     * Get the latest document based on received date and time
     */
    private getLatestDocument(documents: Document[]): Document | null {
        if (documents.length === 0) return null;

        return documents.reduce((latest, current) => {
            const latestDateTime = this.getDocumentDateTime(latest);
            const currentDateTime = this.getDocumentDateTime(current);

            return currentDateTime > latestDateTime ? current : latest;
        });
    }

    /**
     * Get document date time for comparison
     * Combines receivedDate and receivedDateTime if available
     */
    private getDocumentDateTime(doc: Document): Date {
        const isoDate = this.toISODate(doc.receivedDate);
        if (!isoDate) return new Date(0);
        const [y, m, d] = isoDate.split('-').map(Number);
        return new Date(y, m - 1, d);
    }

    /**
     * View document PDF in dialog
     */
    private viewDocument(doc: Document) {
        const downloadRequest = {
            attachmentDocId: doc.attachmentDocId,
            downloadall: 'no',
            policyNo: this.currentPolicyNo,
            token: getAuthToken(),
            viewdocument: 'yes',
        };

        this.loader.showLoading();
        this.documentService
            .downloadDocument(downloadRequest)
            .pipe(finalize(() => this.loader.hideLoading()))
            .subscribe({
                next: (data: any) => {
                    if (data.statusCode === 200 && data.linkArr && data.linkArr.length > 0) {
                        const pdfUrl = this.documentService.getDocumentUrl(data.linkArr[0].link);
                        this.openPdfDialog(
                            pdfUrl,
                            doc.fileName,
                            doc.attachmentDocId,
                            doc.category,
                            doc.attachmentDocType
                        );
                    } else {
                        this.toastService.add({
                            type: 'error',
                            title: '',
                            message: data.responseMessage || 'Error while opening the document.',
                        });
                    }
                },
                error: () => {
                    this.toastService.add({
                        type: 'error',
                        title: '',
                        message: 'Error while opening the document.',
                    });
                },
            });
    }

    /**
     * Open PDF in a dialog window
     */
    private openPdfDialog(
        pdfUrl: string,
        fileName: string,
        attachmentDocId: string,
        documentCategory: string,
        attachmentDocType: string
    ): void {
        if (this.currentDialogRef) {
            this.dialogSubscriptions.unsubscribe();
            this.dialogSubscriptions = new Subscription();
            this.currentDialogRef.close();
        }

        const uniqueClass = `pdf-dialog-${Date.now()}-${Math.random().toString(36).substring(2, 11)}`;

        this.currentDialogRef = this.dialog.open(PdfViewerComponent, {
            data: {
                pdfUrl,
                fileName,
                attachmentDocId,
                policyNo: this.currentPolicyNo,
                documentCategory,
                attachmentDocType,
                subCategory: documentCategory,
            },
            width: '34%',
            height: '80%',
            panelClass: ['pdf-viewer-dialog', uniqueClass],
            maxWidth: '100vw',
            hasBackdrop: false,
            disableClose: false,
            scrollStrategy: this.scrollStrategy.noop(),
            position: { top: '5%', right: '5%' },
        });

        this.setupPinButton(uniqueClass, pdfUrl);
        this.setupDialogCleanup();
    }

    /**
     * Setup pin button for PDF dialog
     */
    private setupPinButton(uniqueClass: string, pdfUrl: string): void {
        const openSub = this.currentDialogRef.afterOpened().subscribe(() => {
            const dialogElement = document.querySelector(`.${uniqueClass}`);
            if (!dialogElement?.querySelector('.external-pin-button')) {
                const pinButton = this.renderer.createElement('button');
                this.renderer.addClass(pinButton, 'external-pin-button');
                this.iconRegistry.getNamedSvgIcon('pin').pipe(take(1)).subscribe((svgElement) => {
                    this.renderer.appendChild(pinButton, svgElement);
                });
                this.renderer.listen(pinButton, 'click', () => {
                    window.open(pdfUrl, '_blank');
                    this.currentDialogRef.close();
                });
                this.renderer.appendChild(dialogElement, pinButton);
            }
        });
        this.dialogSubscriptions.add(openSub);
    }

    /**
     * Setup dialog cleanup on close
     */
    private setupDialogCleanup(): void {
        const closeSub = this.currentDialogRef.afterClosed().subscribe(() => {
            this.currentDialogRef = null;
            this.dialogSubscriptions.unsubscribe();
            this.dialogSubscriptions = new Subscription();
        });
        this.dialogSubscriptions.add(closeSub);
    }

    loadMore() {
        this.showAll = true;
        this.displayedInspections = this.inspections;
    }

    showLess() {
        this.showAll = false;
        this.displayedInspections = this.inspections.slice(0, 3);
        this.el.nativeElement.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }

    ngOnDestroy() {
        this.combinedSubscription?.unsubscribe();
        this.dialogSubscriptions.unsubscribe();
        this.currentDialogRef?.close();
    }

    retryDCPolicy() {
        this.retry.emit();
    }
}
